import { Phone, Volume2, VolumeX } from "lucide-react";
import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { playChime, setSoundOn, soundOn } from "@/lib/chime";
import { cn } from "@/lib/utils";

import type { TaskCard } from "../types";
import { formatTime } from "./cardStatus";
import { fullName } from "./hooks";
import { FunnelShell } from "./shared";
import { useTaskActions } from "./taskActions";
import { TaskRow } from "./TaskRow";
import { endOfTodayIso, taskTiming, type TaskTiming } from "./todayLogic";

type Tab = "today" | "overdue" | "all";

const SECTIONS: { timing: TaskTiming; key: string }[] = [
  { timing: "overdue", key: "crm.tasks_page.overdue" },
  { timing: "today", key: "crm.tasks_page.today" },
  { timing: "later", key: "crm.tasks_page.later" },
  { timing: "none", key: "crm.tasks_page.no_date" },
];

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

/** "через 12 хв", "через 2 год", "прострочено 5 хв" */
const useCountdown = (due: string | null) => {
  const translate = useTranslate();
  const [, tick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => tick((n) => n + 1), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  if (!due) return null;
  const minutes = Math.round((new Date(due).getTime() - Date.now()) / 60_000);
  const abs = Math.abs(minutes);
  const amount =
    abs < 60
      ? translate("crm.tasks_page.min_short", { count: abs })
      : translate("crm.tasks_page.hour_short", { count: Math.round(abs / 60) });
  return minutes >= 0
    ? translate("crm.tasks_page.in", { time: amount })
    : translate("crm.tasks_page.late", { time: amount });
};

/** My open tasks (docs/design/TasksM.dc.html), built to make finishing them pleasant */
export const TasksPage = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const [tab, setTab] = useState<Tab>("today");
  const [sound, setSound] = useState(soundOn());
  const now = new Date();
  const { data: tasks = [] } = useGetList<TaskCard>(
    "task_cards",
    {
      filter: { "done_date@is": null, sales_id: identity?.id },
      pagination: { page: 1, perPage: 500 },
      sort: { field: "due_date", order: "ASC" },
    },
    { enabled: identity?.id != null, refetchInterval: 60_000 },
  );
  const { total: doneToday = 0 } = useGetList<TaskCard>(
    "task_cards",
    {
      filter: { "done_date@gte": startOfToday(), sales_id: identity?.id },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: identity?.id != null },
  );
  const dueToday = tasks.filter(
    (t) => t.due_date && t.due_date <= endOfTodayIso(now),
  );
  const totalToday = dueToday.length + doneToday;
  const progress = totalToday ? Math.round((doneToday / totalToday) * 100) : 0;
  const focus = dueToday[0] ?? tasks[0];

  // a little celebration the moment the last task of the day is done
  const hadTasks = useRef(false);
  useEffect(() => {
    if (dueToday.length > 0) hadTasks.current = true;
    else if (hadTasks.current && doneToday > 0) {
      hadTasks.current = false;
      window.setTimeout(() => playChime("done"), 500);
    }
  }, [dueToday.length, doneToday]);

  const visible: Record<Tab, TaskTiming[]> = {
    today: ["overdue", "today"],
    overdue: ["overdue"],
    all: ["overdue", "today", "later", "none"],
  };
  const groups = SECTIONS.filter((s) => visible[tab].includes(s.timing))
    .map((s) => ({
      ...s,
      tasks: tasks.filter(
        (t) => taskTiming(t.due_date, now) === s.timing && t.id !== focus?.id,
      ),
    }))
    .filter((g) => g.tasks.length > 0);

  return (
    <FunnelShell
      title={translate("crm.tasks_page.title")}
      actions={
        <button
          type="button"
          onClick={() => {
            setSoundOn(!sound);
            setSound(!sound);
            if (!sound) playChime("nudge");
          }}
          className="ml-auto flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm text-muted-foreground"
        >
          {sound ? (
            <Volume2 className="size-4" />
          ) : (
            <VolumeX className="size-4" />
          )}
          {translate(
            sound ? "crm.tasks_page.sound_on" : "crm.tasks_page.sound_off",
          )}
        </button>
      }
    >
      <div className="flex max-w-5xl flex-col gap-5">
        <div className="flex items-center gap-4 rounded-2xl border bg-card p-4">
          <ProgressRing value={progress} />
          <div className="min-w-0">
            <div className="text-lg font-extrabold">
              {totalToday === 0
                ? translate("crm.tasks_page.free_day")
                : translate("crm.tasks_page.progress", {
                    done: doneToday,
                    total: totalToday,
                  })}
            </div>
            <div className="text-sm text-muted-foreground">
              {dueToday.length === 0 && doneToday > 0
                ? translate("crm.tasks_page.all_done")
                : translate("crm.tasks_page.left_today", {
                    smart_count: dueToday.length,
                  })}
            </div>
          </div>
        </div>

        {focus ? <FocusCard task={focus} /> : null}

        <div
          role="tablist"
          className="grid grid-cols-3 rounded-xl border bg-card p-1 text-sm md:max-w-xl"
        >
          {(["today", "overdue", "all"] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              onClick={() => setTab(value)}
              className={cn(
                "rounded-lg py-2 font-semibold",
                tab === value
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground",
              )}
            >
              {translate(`crm.tasks_page.tab_${value}`)}
            </button>
          ))}
        </div>

        {groups.map((group) => (
          <section key={group.timing} className="flex flex-col gap-3">
            <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {translate(group.key)}
            </h2>
            {group.tasks.map((task) => (
              <TaskRow key={task.id} task={task} />
            ))}
          </section>
        ))}
        {groups.length === 0 && !focus ? (
          <div className="rounded-2xl border border-dashed p-8 text-center">
            <div className="text-4xl">🎉</div>
            <div className="mt-2 font-bold">
              {translate("crm.tasks_page.empty_title")}
            </div>
            <p className="text-sm text-muted-foreground">
              {translate("crm.tasks_page.empty")}
            </p>
          </div>
        ) : null}
      </div>
    </FunnelShell>
  );
};

const ProgressRing = ({ value }: { value: number }) => {
  const r = 22;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 56 56" className="size-14 shrink-0 -rotate-90">
      <circle
        cx="28"
        cy="28"
        r={r}
        fill="none"
        strokeWidth="6"
        className="stroke-muted"
      />
      <circle
        cx="28"
        cy="28"
        r={r}
        fill="none"
        strokeWidth="6"
        strokeLinecap="round"
        stroke="#2f7a50"
        strokeDasharray={c}
        strokeDashoffset={c - (c * value) / 100}
        className="transition-[stroke-dashoffset] duration-700"
      />
      <text
        x="28"
        y="28"
        textAnchor="middle"
        dominantBaseline="central"
        className="rotate-90 fill-foreground text-[13px] font-bold"
        style={{ transformOrigin: "28px 28px" }}
      >
        {value}%
      </text>
    </svg>
  );
};

/** The next thing to do, big, with a countdown and the call button */
const FocusCard = ({ task }: { task: TaskCard }) => {
  const translate = useTranslate();
  const actions = useTaskActions();
  const countdown = useCountdown(task.due_date);
  const late = task.due_date ? new Date(task.due_date) < new Date() : false;
  return (
    <div
      className={cn(
        "rounded-2xl border-2 p-5",
        late
          ? "border-destructive/50 bg-destructive/5"
          : "border-[#2f7a50]/40 bg-[#e3f1e8]/60 dark:bg-[#1c3a29]/60",
      )}
    >
      <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {translate("crm.tasks_page.focus")}
        {countdown ? ` · ${countdown}` : ""}
        {task.due_date ? ` · ${formatTime(new Date(task.due_date))}` : ""}
      </div>
      <div className="mt-1 text-xl font-extrabold">{task.text}</div>
      <div className="text-muted-foreground">
        {task.deal_id ? (
          <Link to={`/funnel/${task.deal_id}`} className="underline">
            {fullName(task)}
          </Link>
        ) : (
          fullName(task)
        )}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {task.phone ? (
          <a
            href={`tel:${task.phone}`}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 font-semibold text-primary-foreground"
          >
            <Phone className="size-4" />
            {translate("crm.funnel.lead.call")}
          </a>
        ) : null}
        <button
          type="button"
          onClick={() => actions.done(task)}
          className="rounded-xl bg-[#2f7a50] px-4 py-2 font-semibold text-white"
        >
          ✓ {translate("crm.tasks_page.action_done")}
        </button>
        <button
          type="button"
          onClick={() => actions.later(task, 60)}
          className="rounded-xl border bg-card px-4 py-2 font-semibold"
        >
          +1 {translate("crm.tasks_page.hour")}
        </button>
        <button
          type="button"
          onClick={() => actions.tomorrow(task)}
          className="rounded-xl border bg-card px-4 py-2 font-semibold"
        >
          {translate("crm.tasks_page.tomorrow")}
        </button>
      </div>
    </div>
  );
};
